import React, {ReactElement, ReactNode} from "react";
import {navigate} from "gatsby";

import {FormData} from "../../lead-forms/lead-form/LeadForm";
import {CTA} from "../../buttons/cta-button/CTAButton";
import {ImageObject} from "../../image/Image";
import HeroSideBySideModule, {
  HeroSideBySideModuleLayout,
  HeroSideBySideModuleTheme,
} from "../../hero-side-by-side-module/HeroSideBySideModule";
import FAQAccordion from "../../accordion/faq-accordion/FAQAccordion";
import FeaturedContentModule from "../../featured-content-module/FeaturedContentModule";
import Quotes, {parseContentfulQuoteData} from "../../quotes/Quotes";
import PartnershipModule, {
  PartnershipModuleProps,
} from "../../partnership-module/PartnershipModule";
import FastFactsColumns, {
  FastFactsColumnsProps,
} from "../../fast-facts/fast-facts-columns/FastFactsColumns";
import FastFactCollection from "../../fast-facts/fast-fact-collection/FastFactCollection";
import OpenEmissionsCalculator from "../../open-emissions-calculator/OpenEmissionsCalculator";
import ImageVideoTextStacked, {
  ImageVideoTextStackedProps,
} from "../../image-video-text-stacked/ImageVideoTextStacked";
import ScrollTakeoverModule from "../../scroll-takeover-module/ScrollTakeoverModule";
import Video, {VideoType} from "../../videos/video/Video";
import GetStartedModule, {
  GetStartedModuleProps,
} from "../../get-started-module/GetStartedModule";
import CopyModule from "../../copy-module/CopyModule";
import FlexibleBaseContentModel, {
  FlexibleBaseContentModelProps,
} from "../../flexible-base-content-model/FlexibleBaseContentModel";
import Callout from "../../callout/Callout";
import MarkdownText, {
  MARKDOWN_TYPES,
} from "../../markdown/markdown-text/MarkdownText";
import Quote from "../../cards/quote/Quote";
import SplitModule, {SplitModuleProps} from "../../split-module/SplitModule";
import CardCarousel from "../../carousels/card-carousel/CardCarousel";
import LinkCollection from "../../link-collection/LinkCollection";
import GlobalReachMap from "../../global-reach-map/GlobalReachMap";
import CargoInsuranceCards from "../../cargo-insurance-cards/CargoInsuranceCards";
import CareersThreeColumnList from "../../careers-three-column-list/CareersThreeColumnList";
import {Carousel} from "../../carousels/carousel/Carousel";
import buildGetStartedModuleFragmentFragment from "/src/contentful/fixture-builders/get-started-module";
import {
  pickTestComponent,
  TEST_CONTAINER,
  TestContainer,
} from "/src/utils/ab-testing";
import ModuleBody from "../module-body/ModuleBody";
import WidgetBreadcrumbs, {
  WidgetBreadcrumbsLink,
} from "../../widgets/widget-breadcrumbs/WidgetBreadcrumbs";

import * as styles from "./sections.module.scss";

interface ContentPageVideoType
  extends Pick<
    Queries.ContentfulContentPageVideo,
    "videoControls" | "videoSource" | "caption" | "popup" | "autoplay"
  > {
  __typename: string;
}

interface ContentPageBody
  extends Pick<Queries.ContentfulContentPageBodyCopy, "body"> {
  __typename: string;
}

interface ContentPagePullQuote
  extends Pick<
    Queries.ContentfulContentPagePullQuote,
    "quoteText" | "authorName" | "authorTitle"
  > {
  __typename: string;
}

export type Section =
  | ContentPageBody
  | ContentPagePullQuote
  | ContentPageVideoType
  | Queries.CalloutFragmentFragment
  | Queries.CardCarouselFragmentFragment
  | Queries.CareersThreeColumnListFragmentFragment
  | Queries.CargoInsuranceCardsFragmentFragment
  | Queries.CarouselFragmentFragment
  | Queries.CopyModuleFragmentFragment
  | Queries.CopyModuleFragmentFragment
  | Queries.FaqFragmentFragment
  | Queries.FastFactCollectionFragmentFragment
  | Queries.FastFactsFragmentFragment
  | Queries.FeaturedContentModuleCardAsGetStartedModuleFragmentFragment
  | Queries.FeaturedContentModuleFragmentFragment
  | Queries.FlexibleBaseContentModelFragmentFragment
  | Queries.GetStartedModuleFragmentFragment
  | Queries.GlobalReachMapFragmentFragment
  | Queries.HeroSideBySideModuleFragmentFragment
  | Queries.ImageVideoTextStackedFragmentFragment
  | Queries.LinkCollectionFragmentFragment
  | Queries.PartnershipModuleFragmentFragment
  | Queries.QuoteModuleFragmentFragment
  | Queries.ScrollTakeoverModuleFragmentFragment
  | Queries.SplitModuleFragmentFragment
  | Queries.CarouselFragmentFragment
  | Queries.OpenEmissionsCalculatorFragmentFragment;

interface SectionsProp {
  sections: Array<Section>;
  isHomePage?: boolean;
  slug?: string;
  parentPath?: string;
  skipHeaderFlexibleBaseContentModel?: boolean;
  typeFlexibleBaseContentModel?: string;
  onLeadFormSubmitSuccess?: (data: FormData) => unknown;
  onLeadFormSubmitError?: (err: Error) => unknown;
  careersSearchModule?: ReactNode;
}

const Sections = (props: SectionsProp): ReactElement => {
  const {
    isHomePage,
    onLeadFormSubmitError,
    onLeadFormSubmitSuccess,
    sections,
    slug,
    typeFlexibleBaseContentModel,
    skipHeaderFlexibleBaseContentModel,
    parentPath,
    careersSearchModule,
  } = props;

  const entries = (sections || []).map(section => {
    if (section?.__typename === TEST_CONTAINER) {
      return pickTestComponent(section as unknown as TestContainer)?.component;
    }

    return section;
  });

  return (
    // there is no need to use suspense at the moment, unless we figured out how
    // to solve the rehydration issue when we dynamically import the component
    <React.Fragment>
      {(entries || []).map((section, i) => {
        const title = `${(section as Section)?.__typename!}-${i}`;
        const sectionType = (section as Section)?.__typename!;

        switch (sectionType) {
          case "ContentfulHeroSideBySideModule": {
            const data =
              section as Queries.HeroSideBySideModuleFragmentFragment;

            const successFormDefault = () => {
              if (data.leadFormConfirmationRoute != null) {
                navigate(data.leadFormConfirmationRoute);
              } else {
                navigate("/thank-you/confirmation-page/");
              }
            };

            const errorFormDefault = () => {};
            return (
              <section
                key={title}
                {...(data.anchorLinkId ? {id: data.anchorLinkId} : {})}
              >
                <HeroSideBySideModule
                  key={title}
                  {...data}
                  isHomePage={isHomePage === true}
                  layout={data.layout as HeroSideBySideModuleLayout}
                  theme={data.theme as HeroSideBySideModuleTheme}
                  videoBehavior={data.videoBehavior as VideoType}
                  onLeadFormSubmitSuccess={
                    onLeadFormSubmitSuccess || successFormDefault
                  }
                  onLeadFormSubmitError={
                    onLeadFormSubmitError || errorFormDefault
                  }
                />
              </section>
            );
          }
          case "ContentfulScrollTakeoverModule": {
            const data =
              section as Queries.ScrollTakeoverModuleFragmentFragment;
            return <ScrollTakeoverModule {...data} key={title} />;
          }
          case "ContentfulImageVideoTextStacked": {
            const data = section as ImageVideoTextStackedProps;
            return <ImageVideoTextStacked {...data} key={title} />;
          }
          case "ContentfulFaq":
            return (
              <FAQAccordion
                key={title}
                data={section as Queries.FaqFragmentFragment}
              />
            );
          case "ContentfulFastFacts":
            const data = section as FastFactsColumnsProps;
            return <FastFactsColumns {...data} key={title} />;
          case "ContentfulQuoteModule": {
            const parsedData = parseContentfulQuoteData(
              section as Queries.QuoteModuleFragmentFragment
            );

            return <Quotes key={title} {...parsedData} />;
          }
          case "ContentfulPartnershipModule": {
            const data = section as PartnershipModuleProps;
            return <PartnershipModule key={title} {...data} />;
          }
          case "ContentfulGetStartedModule": {
            return (
              <GetStartedModule
                key={title}
                {...(section as GetStartedModuleProps)}
              />
            );
          }
          case "ContentfulFeaturedContentModuleCard": {
            // this is a legacy data used in landing page template
            // it was using Mustache component, which is essentially
            // get started module component
            const temp =
              section as Queries.FeaturedContentModuleCardAsGetStartedModuleFragmentFragment;

            let ctas: Array<Queries.CtaButtonFragmentFragment> = [
              temp.ctaLink!,
            ];

            if (temp.ctaButtons) {
              ctas =
                temp.ctaButtons as Array<Queries.CtaButtonFragmentFragment>;
            }

            const props = buildGetStartedModuleFragmentFragment({
              title: temp.title!,
              body: {
                body: temp.aliasCopy?.copy || temp.eyebrow,
              },
              ctaButtons: ctas,
              layout: "plain",
              videoBehavior: null,
            }) as GetStartedModuleProps;

            return <GetStartedModule {...props} key={title} />;
          }
          case "ContentfulFeaturedContentModule":
            return (
              <FeaturedContentModule
                key={title}
                {...(section as Queries.FeaturedContentModuleFragmentFragment)}
              />
            );
          case "ContentfulSplitModule": {
            return (
              <React.Fragment key={title}>
                {careersSearchModule}
                <SplitModule key={title} {...(section as SplitModuleProps)} />
              </React.Fragment>
            );
          }
          case "ContentfulFlexibleBaseContentModel": {
            const temp = {
              ...section,
              slug: slug!,
              skipHeaderTags: skipHeaderFlexibleBaseContentModel,
              articleType: typeFlexibleBaseContentModel || "blog",
            } as FlexibleBaseContentModelProps;
            return <FlexibleBaseContentModel {...temp} key={title} />;
          }
          case "ContentfulContentPageBodyCopy": {
            const temp = section as ContentPageBody;
            return (
              <ModuleBody classes={styles.sections__body} key={title}>
                <MarkdownText
                  type={MARKDOWN_TYPES.ARTICLE}
                  text={temp.body?.body!}
                />
              </ModuleBody>
            );
          }
          case "ContentfulContentPageVideo": {
            const temp = section as ContentPageVideoType;

            let type = "inline";
            if (temp.popup) {
              type = "popup";
            } else if (temp.autoplay) {
              type = "autoplay";
            }

            return (
              <ModuleBody classes={styles.sections__body} key={title}>
                <Video
                  videoTitle={title!}
                  showControl={temp.videoControls === true}
                  type={type as VideoType}
                  videoSource={temp.videoSource!}
                  caption={temp.caption?.caption!}
                />
              </ModuleBody>
            );
          }
          case "ContentfulContentPagePullQuote": {
            const temp = section as ContentPagePullQuote;
            return (
              <ModuleBody classes={styles.sections__body} key={title}>
                <Quote
                  name={temp.authorName!}
                  role={temp.authorTitle!}
                  quote={temp.quoteText?.quoteText!}
                />
              </ModuleBody>
            );
          }
          case "ContentfulCallout": {
            const temp = section as Queries.CalloutFragmentFragment;
            return (
              <Callout
                headline={temp.copy?.copy!}
                cta={temp.ctaLink as CTA}
                image={temp.thumbnail as ImageObject}
                key={title}
              />
            );
          }

          case "ContentfulBreadcrumbs": {
            const temp = section as Queries.BreadcrumbsFragmentFragment;

            const breadcrumbLinks: WidgetBreadcrumbsLink[] = (temp.links || [])
              .map(
                (ctaButtonNode: Queries.CtaButtonFragmentFragment | null) => {
                  if (!ctaButtonNode) {
                    return null;
                  }

                  const {label, url} = ctaButtonNode;

                  if (typeof label !== "string" || label.trim() === "") {
                    return null;
                  }

                  return {
                    label: label,
                    href: url || undefined,
                  };
                }
              )
              .filter(link => link !== null) as WidgetBreadcrumbsLink[];

            if (breadcrumbLinks.length === 0) {
              return null;
            }

            return (
              <ModuleBody classes={styles.sections__breadcrumbs} key={title}>
                <WidgetBreadcrumbs links={breadcrumbLinks} />
              </ModuleBody>
            );
          }

          case "ContentfulCopyModule": {
            const temp = section as Queries.CopyModuleFragmentFragment;
            return (
              <ModuleBody classes={styles.sections__body} key={title}>
                <CopyModule
                  eyebrowImg={temp.eyebrowLogo as ImageObject}
                  eyebrow={temp.eyebrow!}
                  title={temp.title!}
                  body={temp.body?.body!}
                  fullWidth={temp.fullWidth!}
                  callout={{
                    headline: temp.callout?.copy?.copy!,
                    cta: temp.callout?.ctaLink as CTA,
                    image: temp.callout?.thumbnail as ImageObject,
                  }}
                  cta={temp.ctaButton as CTA}
                />
              </ModuleBody>
            );
          }

          case "ContentfulCargoInsuranceCards": {
            const temp = section as Queries.CargoInsuranceCardsFragmentFragment;
            return (
              <CargoInsuranceCards key={title} {...temp} path={parentPath!} />
            );
          }

          case "ContentfulGlobalReachMap": {
            const temp = section as Queries.GlobalReachMapFragmentFragment;
            return <GlobalReachMap key={title} {...temp} />;
          }

          case "ContentfulLinkCollection": {
            const temp = section as Queries.LinkCollectionFragmentFragment;
            return <LinkCollection key={title} {...temp} />;
          }

          case "ContentfulCardCarousel": {
            const temp = section as Queries.CardCarouselFragmentFragment;
            return <CardCarousel key={title} {...temp} />;
          }

          case "ContentfulCareersThreeColumnList": {
            const temp =
              section as Queries.CareersThreeColumnListFragmentFragment;
            const items = temp.items?.map(item => ({
              title: item?.title || undefined,
              gifUrl: item?.gifUrl || undefined,
              copy: item?.copy?.copy || undefined,
              cta: item?.cta as CTA,
            }));

            return (
              <CareersThreeColumnList
                key={title}
                title={temp.title!}
                copy={temp.copy?.copy!}
                items={items}
                anchorLinkId={temp.anchorLinkId}
              />
            );
          }

          case "ContentfulCarousel": {
            const temp = section as Queries.CarouselFragmentFragment;

            return (
              <Carousel
                key={title}
                {...temp}
                highlightedButtonShape={temp.highlightedButtonShape}
                textTransition={temp.textTransition}
              />
            );
          }

          case "ContentfulFastFactCollection": {
            const temp = section as Queries.FastFactCollectionFragmentFragment;

            return <FastFactCollection key={title} {...temp} />;
          }

          case "ContentfulOpenEmissionsCalculator": {
            return null;
          }

          default:
            return null;
        }
      })}
    </React.Fragment>
  );
};

export default Sections;

// This is the lazy load version
// for some reason, it keeps giving us rehydration error, especially
// on slow network
// commenting this out for now, to be re-visited once we address
// other performance issues
// TODO: do lazy load of component

// import React, {ReactElement, ReactNode} from "react";
// import {navigate} from "gatsby";

// import {SplitModuleProps} from "../../split-module/SplitModule";
// import {
//   HeroSideBySideModuleLayout,
//   HeroSideBySideModuleTheme,
// } from "../../hero-side-by-side-module/HeroSideBySideModule";
// import {parseContentfulQuoteData} from "../../quotes/Quotes";
// import {PartnershipModuleProps} from "../../partnership-module/PartnershipModule";
// import {FastFactsColumnsProps} from "../../fast-facts/fast-facts-columns/FastFactsColumns";
// import {ImageVideoTextStackedProps} from "../../image-video-text-stacked/ImageVideoTextStacked";
// import {VideoType} from "../../videos/video/Video";
// import {GetStartedModuleProps} from "../../get-started-module/GetStartedModule";
// import {FormData} from "../../lead-forms/lead-form/LeadForm";
// import {FlexibleBaseContentModelProps} from "../../flexible-base-content-model/FlexibleBaseContentModel";
// import {MARKDOWN_TYPES} from "../../markdown/markdown-text/MarkdownText";
// import {CTA} from "../../buttons/cta-button/CTAButton";
// import {ImageObject} from "../../image/Image";
// import buildGetStartedModuleFragmentFragment from "/src/contentful/fixture-builders/get-started-module";

// const HeroSideBySideModule = React.lazy(
//   () => import("../../hero-side-by-side-module/HeroSideBySideModule")
// );
// const FAQAccordion = React.lazy(
//   () => import("../../accordion/faq-accordion/FAQAccordion")
// );
// const FeaturedContentModule = React.lazy(
//   () => import("../../featured-content-module/FeaturedContentModule")
// );
// const Quotes = React.lazy(() => import("../../quotes/Quotes"));
// const PartnershipModule = React.lazy(
//   () => import("../../partnership-module/PartnershipModule")
// );
// const FastFactsColumns = React.lazy(
//   () => import("../../fast-facts/fast-facts-columns/FastFactsColumns")
// );
// const ImageVideoTextStacked = React.lazy(
//   () => import("../../image-video-text-stacked/ImageVideoTextStacked")
// );
// const ScrollTakeoverModule = React.lazy(
//   () => import("../../scroll-takeover-module/ScrollTakeoverModule")
// );
// const Video = React.lazy(() => import("../../videos/video/Video"));
// const GetStartedModule = React.lazy(
//   () => import("../../get-started-module/GetStartedModule")
// );
// const CopyModule = React.lazy(() => import("../../copy-module/CopyModule"));
// const FlexibleBaseContentModel = React.lazy(
//   () => import("../../flexible-base-content-model/FlexibleBaseContentModel")
// );
// const Callout = React.lazy(() => import("../../callout/Callout"));
// const MarkdownText = React.lazy(
//   () => import("../../markdown/markdown-text/MarkdownText")
// );
// const Quote = React.lazy(() => import("../../cards/quote/Quote"));
// const SplitModule = React.lazy(() => import("../../split-module/SplitModule"));
// const CardCarousel = React.lazy(
//   () => import("../../carousels/card-carousel/CardCarousel")
// );
// const LinkCollection = React.lazy(
//   () => import("../../link-collection/LinkCollection")
// );
// const GlobalReachMap = React.lazy(
//   () => import("../../global-reach-map/GlobalReachMap")
// );
// const CargoInsuranceCards = React.lazy(
//   () => import("../../cargo-insurance-cards/CargoInsuranceCards")
// );
// const CareersThreeColumnList = React.lazy(
//   () => import("../../careers-three-column-list/CareersThreeColumnList")
// );

// interface ContentPageVideoType
//   extends Pick<
//     Queries.ContentfulContentPageVideo,
//     "videoControls" | "videoSource" | "caption" | "popup" | "autoplay"
//   > {
//   __typename: string;
// }

// interface ContentPageBody
//   extends Pick<Queries.ContentfulContentPageBodyCopy, "body"> {
//   __typename: string;
// }

// interface ContentPagePullQuote
//   extends Pick<
//     Queries.ContentfulContentPagePullQuote,
//     "quoteText" | "authorName" | "authorTitle"
//   > {
//   __typename: string;
// }

// export type Section =
//   | ContentPageBody
//   | ContentPagePullQuote
//   | ContentPageVideoType
//   | Queries.CalloutFragmentFragment
//   | Queries.CardCarouselFragmentFragment
//   | Queries.CareersThreeColumnListFragmentFragment
//   | Queries.CargoInsuranceCardsFragmentFragment
//   | Queries.CarouselFragmentFragment
//   | Queries.CopyModuleFragmentFragment
//   | Queries.CopyModuleFragmentFragment
//   | Queries.FaqFragmentFragment
//   | Queries.FastFactCollectionFragmentFragment
//   | Queries.FastFactsFragmentFragment
//   | Queries.FeaturedContentModuleCardAsGetStartedModuleFragmentFragment
//   | Queries.FeaturedContentModuleFragmentFragment
//   | Queries.FlexibleBaseContentModelFragmentFragment
//   | Queries.GetStartedModuleFragmentFragment
//   | Queries.GlobalReachMapFragmentFragment
//   | Queries.HeroSideBySideModuleFragmentFragment
//   | Queries.ImageVideoTextStackedFragmentFragment
//   | Queries.LinkCollectionFragmentFragment
//   | Queries.PartnershipModuleFragmentFragment
//   | Queries.QuoteModuleFragmentFragment
//   | Queries.ScrollTakeoverModuleFragmentFragment
//   | Queries.SplitModuleFragmentFragment;

// interface SectionsProp {
//   sections: Array<Section>;
//   isHomePage?: boolean;
//   slug?: string;
//   parentPath?: string;
//   skipHeaderFlexibleBaseContentModel?: boolean;
//   typeFlexibleBaseContentModel?: string;
//   onLeadFormSubmitSuccess?: (data: FormData) => unknown;
//   onLeadFormSubmitError?: (err: Error) => unknown;
//   careersSearchModule?: ReactNode;
// }

// const Sections = (props: SectionsProp): ReactElement => {
//   const {
//     isHomePage,
//     onLeadFormSubmitError,
//     onLeadFormSubmitSuccess,
//     sections,
//     slug,
//     typeFlexibleBaseContentModel,
//     skipHeaderFlexibleBaseContentModel,
//     parentPath,
//     careersSearchModule,
//   } = props;

//   return (
//     <React.Suspense fallback={null}>
//       {(sections || []).map((section, i) => {
//         const title = `${(section as Section)?.__typename!}-${i}`;
//         const sectionType = (section as Section)?.__typename!;

//         switch (sectionType) {
//           case "ContentfulHeroSideBySideModule": {
//             const data =
//               section as Queries.HeroSideBySideModuleFragmentFragment;

//             const successFormDefault = () => {
//               if (data.leadFormConfirmationRoute != null) {
//                 navigate(data.leadFormConfirmationRoute);
//               } else {
//                 navigate("/thank-you/confirmation-page/");
//               }
//             };

//             const errorFormDefault = () => {};
//             return (
//               <HeroSideBySideModule
//                 key={title}
//                 {...data}
//                 isHomePage={isHomePage === true}
//                 layout={data.layout as HeroSideBySideModuleLayout}
//                 theme={data.theme as HeroSideBySideModuleTheme}
//                 videoBehavior={data.videoBehavior as VideoType}
//                 onLeadFormSubmitSuccess={
//                   onLeadFormSubmitSuccess || successFormDefault
//                 }
//                 onLeadFormSubmitError={
//                   onLeadFormSubmitError || errorFormDefault
//                 }
//               />
//             );
//           }
//           case "ContentfulScrollTakeoverModule": {
//             const data =
//               section as Queries.ScrollTakeoverModuleFragmentFragment;
//             return <ScrollTakeoverModule {...data} key={title} />;
//           }
//           case "ContentfulImageVideoTextStacked": {
//             const data = section as ImageVideoTextStackedProps;
//             return <ImageVideoTextStacked {...data} key={title} />;
//           }
//           case "ContentfulFaq":
//             return (
//               <FAQAccordion
//                 key={title}
//                 data={section as Queries.FaqFragmentFragment}
//               />
//             );
//           case "ContentfulFastFacts":
//             const data = section as FastFactsColumnsProps;
//             return <FastFactsColumns {...data} key={title} />;
//           case "ContentfulQuoteModule": {
//             const parsedData = parseContentfulQuoteData(
//               section as Queries.QuoteModuleFragmentFragment
//             );

//             return <Quotes key={title} {...parsedData} />;
//           }
//           case "ContentfulPartnershipModule": {
//             const data = section as PartnershipModuleProps;
//             return <PartnershipModule key={title} {...data} />;
//           }
//           case "ContentfulGetStartedModule": {
//             return (
//               <GetStartedModule
//                 key={title}
//                 {...(section as GetStartedModuleProps)}
//               />
//             );
//           }
//           case "ContentfulFeaturedContentModuleCard": {
//             // this is a legacy data used in landing page template
//             // it was using Mustache component, which is essentially
//             // get started module component
//             const temp =
//               section as Queries.FeaturedContentModuleCardAsGetStartedModuleFragmentFragment;

//             let ctas: Array<Queries.CtaButtonFragmentFragment> = [
//               temp.ctaLink!,
//             ];

//             if (temp.ctaButtons) {
//               ctas =
//                 temp.ctaButtons as Array<Queries.CtaButtonFragmentFragment>;
//             }

//             const props = buildGetStartedModuleFragmentFragment({
//               title: temp.title!,
//               body: {
//                 body: temp.aliasCopy?.copy || temp.eyebrow,
//               },
//               ctaButtons: ctas,
//               layout: "plain",
//               videoBehavior: null,
//             }) as GetStartedModuleProps;

//             return <GetStartedModule {...props} key={title} />;
//           }
//           case "ContentfulFeaturedContentModule":
//             return (
//               <FeaturedContentModule
//                 key={title}
//                 {...(section as Queries.FeaturedContentModuleFragmentFragment)}
//               />
//             );
//           case "ContentfulSplitModule": {
//             return (
//               <React.Fragment key={title}>
//                 {careersSearchModule}
//                 <SplitModule key={title} {...(section as SplitModuleProps)} />
//               </React.Fragment>
//             );
//           }
//           case "ContentfulFlexibleBaseContentModel": {
//             const temp = {
//               ...section,
//               slug: slug!,
//               skipHeaderTags: skipHeaderFlexibleBaseContentModel,
//               articleType: typeFlexibleBaseContentModel || "blog",
//             } as FlexibleBaseContentModelProps;
//             return <FlexibleBaseContentModel {...temp} key={title} />;
//           }
//           case "ContentfulContentPageBodyCopy": {
//             const temp = section as ContentPageBody;
//             return (
//               <MarkdownText
//                 type={MARKDOWN_TYPES.ARTICLE}
//                 text={temp.body?.body!}
//                 key={title}
//               />
//             );
//           }
//           case "ContentfulContentPageVideo": {
//             const temp = section as ContentPageVideoType;

//             let type = "inline";
//             if (temp.popup) {
//               type = "popup";
//             } else if (temp.autoplay) {
//               type = "autoplay";
//             }

//             return (
//               <Video
//                 videoTitle={title!}
//                 key={title}
//                 showControl={temp.videoControls === true}
//                 type={type as VideoType}
//                 videoSource={temp.videoSource!}
//                 caption={temp.caption?.caption!}
//               />
//             );
//           }
//           case "ContentfulContentPagePullQuote": {
//             const temp = section as ContentPagePullQuote;
//             return (
//               <Quote
//                 key={title}
//                 name={temp.authorName!}
//                 role={temp.authorTitle!}
//                 quote={temp.quoteText!}
//               />
//             );
//           }
//           case "ContentfulCallout": {
//             const temp = section as Queries.CalloutFragmentFragment;
//             return (
//               <Callout
//                 headline={temp.copy?.copy!}
//                 cta={temp.ctaLink as CTA}
//                 image={temp.thumbnail as ImageObject}
//                 key={title}
//               />
//             );
//           }
//           case "ContentfulCopyModule": {
//             const temp = section as Queries.CopyModuleFragmentFragment;
//             return (
//               <CopyModule
//                 eyebrowImg={temp.eyebrowLogo as ImageObject}
//                 eyebrow={temp.eyebrow!}
//                 title={temp.title!}
//                 body={temp.body?.body!}
//                 fullWidth={temp.fullWidth!}
//                 callout={{
//                   headline: temp.callout?.copy?.copy!,
//                   cta: temp.callout?.ctaLink as CTA,
//                   image: temp.callout?.thumbnail as ImageObject,
//                 }}
//                 key={title}
//                 cta={temp.ctaButton as CTA}
//               />
//             );
//           }

//           case "ContentfulCargoInsuranceCards": {
//             const temp = section as Queries.CargoInsuranceCardsFragmentFragment;
//             return (
//               <CargoInsuranceCards key={title} {...temp} path={parentPath!} />
//             );
//           }

//           case "ContentfulGlobalReachMap": {
//             const temp = section as Queries.GlobalReachMapFragmentFragment;
//             return <GlobalReachMap key={title} {...temp} />;
//           }

//           case "ContentfulLinkCollection": {
//             const temp = section as Queries.LinkCollectionFragmentFragment;
//             return <LinkCollection key={title} {...temp} />;
//           }

//           case "ContentfulCardCarousel": {
//             const temp = section as Queries.CardCarouselFragmentFragment;
//             return <CardCarousel key={title} {...temp} />;
//           }

//           case "ContentfulCareersThreeColumnList": {
//             const temp =
//               section as Queries.CareersThreeColumnListFragmentFragment;
//             const items = temp.items?.map(item => ({
//               title: item?.title || undefined,
//               gifUrl: item?.gifUrl || undefined,
//               copy: item?.copy?.copy || undefined,
//               cta: item?.cta as CTA,
//             }));

//             return (
//               <CareersThreeColumnList
//                 key={title}
//                 title={temp.title!}
//                 copy={temp.copy?.copy!}
//                 items={items}
//               />
//             );
//           }

//           default:
//             return null;
//         }
//       })}
//     </React.Suspense>
//   );
// };

// export default Sections;
